import type { RouteLocationNormalizedGeneric } from '#vue-router'

import { useOrdersStore } from '@widgets/orders'
import {
  MAX_PER_PAGE,
  getFirstQueryValue,
  normalizeArrayQuery,
  toCountValue,
  toIdValue,
  toPaginationValue,
} from '@shared/lib/routes'
import { parseArrayParam } from '@shared/lib/url'

import { defineNuxtRouteMiddleware, navigateTo } from '#app'

const STATUS_KEY = 'status'
const BRACKET_STATUS_KEY = /^status\[\d+]$/

/**
 * `/orders` — query → store filters, mirroring `middleware/promocodes.ts`. The default status set
 * lives on the sidebar link, so a bare `/orders` deliberately means "every status".
 */
export default defineNuxtRouteMiddleware((to: RouteLocationNormalizedGeneric) => {
  // Links from app-main and legacy `/admin/orders` bookmarks encode the array as `status[0]=…`;
  // rewrite it once to `status=…` so the filters only ever read one shape.
  const bracketStatuses = parseArrayParam(to.query, STATUS_KEY)

  if (bracketStatuses.length) {
    const query = Object.fromEntries(Object.entries(to.query).filter(([key]) => !BRACKET_STATUS_KEY.test(key)))

    return navigateTo({ path: to.path, query: { ...query, [STATUS_KEY]: bracketStatuses } }, { replace: true })
  }

  const store = useOrdersStore()

  store.setFilterSearch(getFirstQueryValue(to.query?.q) || null)
  store.setFilterStatuses(normalizeArrayQuery(to.query?.status) ?? [])
  store.setFilterType(getFirstQueryValue(to.query?.type) || null)
  store.setFilterFrom(getFirstQueryValue(to.query?.from) || null)
  store.setFilterTo(getFirstQueryValue(to.query?.to) || null)
  store.setFilterLessonsFrom(toCountValue(to.query?.lessons_from))
  store.setFilterLessonsTo(toCountValue(to.query?.lessons_to))
  store.setFilterPromocodeId(toIdValue(to.query?.promocode_id))
  // The customer page narrows the shared store to one customer; a visit here must not inherit it.
  store.setFilterCustomerId(toIdValue(to.query?.customer_id))

  store.setCurrentPage(toPaginationValue(to.query?.page, 1))
  store.setPerPage(toPaginationValue(to.query?.per_page, store.getPerPage, MAX_PER_PAGE))
})
